import { useState, useRef, useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, Send } from "lucide-react";
import { API } from "@/context/AuthContext";
import { AiAnswer } from "@/components/AiAnswer";

const CONTEXT_LABELS = { dashboard: "All portal data", ap: "AP Ageing page", ar: "AR Ageing page", gstr2: "GSTR-2 page" };
const SUGGESTIONS = [
  "Customer and project with the lowest transaction margin last month",
  "Which vendors have invoices overdue beyond 90 days?",
  "Financier maturities due in the next 3 months",
  "Slow-moving stock older than 180 days by plant",
  "Which customers should I follow up first for collections?",
  "How much TDS was deducted last month, section-wise?",
];

export default function AIAgent({ open, onOpenChange, context, prefill }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open) {
      fetch(`${API}/ai/history`, { credentials: "include" })
        .then((r) => r.json())
        .then((hist) => {
          const msgs = [];
          hist.forEach((h) => {
            msgs.push({ role: "user", text: h.question });
            msgs.push({ role: "ai", text: h.answer, sources: h.sources || [] });
          });
          setMessages(msgs);
        })
        .catch(() => {});
    }
  }, [open, context]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (open && prefill) setInput(prefill);
  }, [open, prefill]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }, { role: "ai", text: "", status: "Planning which datasets to use…" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg, context }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const chunks = buf.split("\n\n");
        buf = chunks.pop();
        for (const chunk of chunks) {
          if (!chunk.startsWith("data: ")) continue;
          const d = JSON.parse(chunk.slice(6));
          if (d.delta || d.status || d.sources) {
            setMessages((m) => {
              const copy = [...m];
              const prev = copy[copy.length - 1];
              copy[copy.length - 1] = { ...prev, text: prev.text + (d.delta || ""), status: d.status || prev.status, sources: d.sources || prev.sources };
              return copy;
            });
          }
          if (d.error) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "ai", text: "Sorry, the AI analyst hit an error. Please try again." };
              return copy;
            });
          }
        }
      }
    } catch (e) {
      setMessages((m) => [...m.slice(0, -1), { role: "ai", text: "Connection error. Please try again." }]);
    } finally {
      setStreaming(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-2xl flex flex-col p-0" data-testid="ai-agent-panel">
        <SheetHeader className="px-6 py-4 border-b border-slate-200 bg-slate-900">
          <SheetTitle className="flex items-center gap-2 text-white">
            <Sparkles className="h-4 w-4 text-sky-400" />
            AI Analyst
            <span className="ml-auto text-[10px] font-medium uppercase tracking-widest text-sky-400 bg-sky-950 rounded-full px-2.5 py-1" data-testid="ai-context-badge">
              {CONTEXT_LABELS[context] || "All portal data"}
            </span>
          </SheetTitle>
        </SheetHeader>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4" data-testid="ai-messages-area">
          {messages.length === 0 && (
            <div className="space-y-3 pt-6">
              <p className="text-sm text-slate-500">Ask anything — ageing, ledgers, margins, stock, financing, TDS, GST. Answers come with charts and the datasets used.</p>
              {SUGGESTIONS.map((s, i) => (
                <button
                  key={i}
                  data-testid={`ai-suggestion-${i}`}
                  onClick={() => send(s)}
                  className="block w-full text-left text-sm border border-slate-200 rounded-md px-4 py-3 hover:bg-sky-50 hover:border-sky-300 transition-colors duration-200 text-slate-700"
                >
                  {s}
                </button>
              ))}
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`fade-in-up ${m.role === "user" ? "flex justify-end" : ""}`}>
              {m.role === "user" ? (
                <div className="bg-sky-700 text-white text-sm rounded-lg rounded-br-sm px-4 py-2.5 max-w-[85%]">{m.text}</div>
              ) : (
                <div className="bg-slate-100 text-slate-800 text-sm rounded-lg rounded-bl-sm px-4 py-2.5 w-full" data-testid={`ai-answer-${i}`}>
                  {m.text || m.status ? <AiAnswer text={m.text} sources={m.sources} status={m.status} question={messages[i - 1]?.text} streaming={streaming && i === messages.length - 1} /> : <span className="inline-flex gap-1"><span className="animate-bounce">·</span><span className="animate-bounce [animation-delay:120ms]">·</span><span className="animate-bounce [animation-delay:240ms]">·</span></span>}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="border-t border-slate-200 p-4 flex gap-2">
          <Input
            data-testid="ai-chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Ask the AI analyst..."
            disabled={streaming}
          />
          <Button data-testid="ai-send-button" onClick={() => send()} disabled={streaming || !input.trim()} className="bg-sky-700 hover:bg-sky-800">
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
